import React from "react";
import { useLocation, useNavigate } from "react-router";

import type { AuthUser } from "~/types/auth";
import { login, logout, verifyOtp } from "~/services/authService";
import { stopActiveSipPhone } from "~/services/calling/sipTeardown";
import { clearStoredToken, getStoredToken, storeToken } from "~/services/authStorage";
import { onAuthUnauthorized } from "~/services/authEvents";
import { resetUnauthorizedHandling } from "~/services/apiClient";
import { getProfile } from "~/services/profileService";
import { getSipCredentials } from "~/services/sipService";
import { setUserStatus } from "~/services/userService";
import { readUserSipExtension } from "~/context/call/helpers";

type OtpResendResult = {
  message: string;
};

type LoginStep1Result = {
  otpRequired: boolean;
};

type PendingLoginCredentials = {
  email: string;
  password: string;
};

type AuthContextValue = {
  token: string | null;
  user: AuthUser | null;
  preAuthToken: string | null;
  isAuthenticated: boolean;
  isHydrated: boolean;
  loginStep1: (email: string, password: string) => Promise<LoginStep1Result>;
  loginStep2: (otp: string) => Promise<void>;
  resendOtp: () => Promise<OtpResendResult>;
  logout: (hasActiveCall?: boolean) => Promise<void>;
  clearPreAuthToken: () => void;
  refreshUser: () => Promise<void>;
};

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [token, setToken] = React.useState<string | null>(null);
  const [user, setUser] = React.useState<AuthUser | null>(null);
  const [preAuthToken, setPreAuthToken] = React.useState<string | null>(null);
  const [pendingLoginCredentials, setPendingLoginCredentials] =
    React.useState<PendingLoginCredentials | null>(null);
  const [isHydrated, setIsHydrated] = React.useState(false);

  React.useEffect(() => {
    const stored = getStoredToken();
    if (stored) {
      resetUnauthorizedHandling();
      setToken(stored);
    }
    setIsHydrated(true);
  }, []);

  React.useEffect(() => {
    if (!token || user) return;
    getProfile()
      .then((profile) => {
        setUser(profile);
      })
      .catch(() => {
      });
  }, [token, user]);

  React.useEffect(() => {
    return onAuthUnauthorized(async (detail) => {
      await stopActiveSipPhone();
      clearStoredToken();
      resetUnauthorizedHandling();
      setToken(null);
      setUser(null);
      setPreAuthToken(null);
      setPendingLoginCredentials(null);
      const target = preAuthToken ? "/login/otp" : "/login";
      const message =
        detail.reason === "token-expired"
          ? detail.message ?? "Your session expired. Please sign in again."
          : undefined;
      if (location.pathname !== target) {
        navigate(target, {
          replace: true,
          state: message ? { authMessage: message } : undefined,
        });
      }
    });
  }, [location.pathname, navigate, preAuthToken]);

  const completeAuthenticatedSession = React.useCallback(
    (accessToken: string, authUser: AuthUser) => {
      resetUnauthorizedHandling();
      storeToken(accessToken);
      setToken(accessToken);
      setUser(authUser);
      setPreAuthToken(null);
      setPendingLoginCredentials(null);

      void getProfile()
        .then((profile) => {
          setUser(profile);
        })
        .catch(() => {
        });
      void setUserStatus("Online").catch(() => {});
      void getSipCredentials().catch(() => {});
    },
    []
  );

  const loginStep1 = React.useCallback(
    async (email: string, password: string): Promise<LoginStep1Result> => {
      const normalizedEmail = email.trim();
      const result = await login(normalizedEmail, password);

      if ("token" in result) {
        completeAuthenticatedSession(result.token, result.user);
        return { otpRequired: false };
      }

      setPendingLoginCredentials({
        email: normalizedEmail,
        password,
      });
      setPreAuthToken(result.preAuthToken);
      return { otpRequired: true };
    },
    [completeAuthenticatedSession]
  );

  const loginStep2 = React.useCallback(
    async (otp: string) => {
      if (!preAuthToken) {
        throw new Error("Missing pre-auth token. Please login again.");
      }
      const { token: accessToken, user: authUser } = await verifyOtp(
        preAuthToken,
        otp
      );
      completeAuthenticatedSession(accessToken, authUser);
    },
    [completeAuthenticatedSession, preAuthToken]
  );

  const resendOtpHandler = React.useCallback(async () => {
    if (!pendingLoginCredentials) {
      throw new Error("Missing login details. Please login again.");
    }
    const result = await login(
      pendingLoginCredentials.email,
      pendingLoginCredentials.password
    );

    if ("token" in result) {
      completeAuthenticatedSession(result.token, result.user);
      return { message: "Signed in successfully." };
    }

    setPreAuthToken(result.preAuthToken);
    return { message: "A new OTP was sent." };
  }, [completeAuthenticatedSession, pendingLoginCredentials]);

  const logoutHandler = React.useCallback(
    async (hasActiveCall?: boolean) => {
      await stopActiveSipPhone();
      if (token) {
        try {
          await logout(hasActiveCall);
        } catch {
          // Ignore logout errors and clear client state anyway.
        }
      }
      clearStoredToken();
      resetUnauthorizedHandling();
      setToken(null);
      setUser(null);
      setPreAuthToken(null);
      setPendingLoginCredentials(null);
      navigate("/login", { replace: true });
    },
    [navigate, token]
  );

  const clearPreAuthToken = React.useCallback(() => {
    setPreAuthToken(null);
    setPendingLoginCredentials(null);
  }, []);

  const refreshUser = React.useCallback(async () => {
    if (!token) return;
    const profile = await getProfile();
    setUser(profile);
  }, [token]);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      token,
      user,
      preAuthToken,
      isAuthenticated: Boolean(token),
      isHydrated,
      loginStep1,
      loginStep2,
      resendOtp: resendOtpHandler,
      logout: logoutHandler,
      clearPreAuthToken,
      refreshUser,
    }),
    [
      token,
      user,
      preAuthToken,
      isHydrated,
      loginStep1,
      loginStep2,
      resendOtpHandler,
      logoutHandler,
      clearPreAuthToken,
      refreshUser,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
